import {
  addIntent,
  deleteIntent,
  getIntent,
  listIntents,
  updateIntent,
  type IntentRecord,
  type IntentStatus,
  type IntentType,
} from '@/lib/db'

export type { IntentRecord, IntentStatus, IntentType }

const CHANGED_EVENT = 'intent-queue-changed'

function emitChanged() {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT))
  } catch { /* noop */ }
}

export interface EnqueueOptions {
  id?: string
  dependsOn?: string
}

export async function enqueueIntent<P extends Record<string, unknown>>(
  type: IntentType,
  payload: P,
  opts?: EnqueueOptions
): Promise<IntentRecord> {
  const id = opts?.id || (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
  const now = Date.now()
  const record: IntentRecord = {
    id,
    type,
    payload,
    dependsOn: opts?.dependsOn,
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
  }
  await addIntent(record)
  emitChanged()
  return record
}

export async function cancelIntent(id: string): Promise<void> {
  await deleteIntent(id)
  emitChanged()
}

export async function cancelPendingIntentsForAnalysis(
  analysisId: string,
  types: IntentType[],
): Promise<number> {
  if (!analysisId || types.length === 0) return 0
  const all = await listIntents()
  let cancelled = 0
  for (const intent of all) {
    if (!types.includes(intent.type)) continue
    if (intent.status !== 'pending' && intent.status !== 'failed') continue
    if (String((intent.payload as { analysisId?: unknown }).analysisId || '') !== analysisId) continue
    await updateIntent(intent.id, { status: 'done' })
    cancelled++
  }
  if (cancelled > 0) emitChanged()
  return cancelled
}

export async function retryIntent(id: string): Promise<void> {
  await updateIntent(id, { status: 'pending', nextRunAt: undefined, lastError: undefined })
  emitChanged()
}

export async function markInFlight(id: string): Promise<IntentRecord | undefined> {
  const record = await updateIntent(id, {
    status: 'in_flight',
    inFlightSince: Date.now(),
  })
  emitChanged()
  return record
}

export async function markDone(id: string): Promise<IntentRecord | undefined> {
  const record = await updateIntent(id, {
    status: 'done',
    inFlightSince: undefined,
  })
  emitChanged()
  return record
}

export async function markFailed(
  id: string,
  error: string,
  nextRunAt?: number,
  terminal = false
): Promise<IntentRecord | undefined> {
  const current = await getIntent(id)
  if (!current) return undefined
  const record = await updateIntent(id, {
    status: terminal ? 'failed' : 'pending',
    inFlightSince: undefined,
    attempts: current.attempts + 1,
    lastError: error,
    nextRunAt,
  })
  emitChanged()
  return record
}

export async function onIntentQueueChanged(handler: () => void): Promise<() => void> {
  if (typeof window === 'undefined') return () => {}
  const listener = () => handler()
  window.addEventListener(CHANGED_EVENT, listener)
  return () => window.removeEventListener(CHANGED_EVENT, listener)
}

export async function pendingIntents(): Promise<IntentRecord[]> {
  return listIntents({ status: 'pending' })
}

export async function inFlightIntents(): Promise<IntentRecord[]> {
  return listIntents({ status: 'in_flight' })
}

export async function allIntents(): Promise<IntentRecord[]> {
  return listIntents()
}

export { CHANGED_EVENT as INTENT_QUEUE_CHANGED_EVENT }

// Block until every upload-image intent for this analysisId is no longer
// pending/in_flight. Resolves immediately if none exist (already done & purged,
// or never queued). Rejects if any has terminally failed. The online detect &
// burst-analyze paths use this to gate `/api/ai/analyze` so the server doesn't
// 400 NO_IMAGE on a row whose upload hasn't landed yet.
export function waitForUploadIntent(
  analysisId: string,
  timeoutMs = 30_000,
): Promise<void> {
  if (!analysisId) return Promise.resolve()

  const isMatch = (i: IntentRecord) =>
    i.type === 'upload-image' &&
    String((i.payload as { analysisId?: unknown }).analysisId || '') === analysisId

  const check = async (): Promise<'done' | 'pending' | 'failed'> => {
    const all = await listIntents()
    const matches = all.filter(isMatch)
    if (matches.length === 0) return 'done'
    if (matches.some((i) => i.status === 'failed')) return 'failed'
    if (matches.every((i) => i.status === 'done')) return 'done'
    return 'pending'
  }

  return new Promise<void>((resolve, reject) => {
    let settled = false
    let unsubscribe: (() => void) | null = null
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      unsubscribe?.()
      reject(new Error('upload_timeout'))
    }, timeoutMs)

    const settle = (ok: boolean, err?: Error) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe?.()
      if (ok) resolve()
      else reject(err ?? new Error('upload_failed'))
    }

    const evaluate = () => {
      check()
        .then((state) => {
          if (state === 'done') settle(true)
          else if (state === 'failed') settle(false, new Error('upload_failed'))
        })
        .catch(() => { /* keep waiting; next event re-evaluates */ })
    }

    if (typeof window !== 'undefined') {
      const listener = () => evaluate()
      window.addEventListener(CHANGED_EVENT, listener)
      unsubscribe = () => window.removeEventListener(CHANGED_EVENT, listener)
    }
    evaluate()
  })
}
